import { Component, OnInit, OnDestroy, inject, ElementRef, AfterViewInit, ChangeDetectorRef, computed, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderLogoComponent } from '../header-logo/header-logo.component';
import { HeaderNavigationalButtonComponent } from '../header-navigational-button/header-navigational-button.component';
import { OrderDetailsButtonComponent } from '../order-details-button/order-details-button.component';
import { AccountButtonComponent } from '../account-button/account-button.component';
import { ViewOrderButtonComponent } from '../view-order-button/view-order-button.component';
import { IconComponent } from '../icons/icon.component';
import { IconNameType } from '../icons/icon-name-type.enum';
import { RoundIconType } from '../round-icon/round-icon-type.enum';
import { slideInLeft } from '../animations/animations';
import { LayoutService } from '../../core/services/layout.service';
import { OrderService } from '../../core/services/order.service';
import { Router } from '@angular/router';
import { StoreIdService } from '../../core/services/store-id.service';
import { ConfigService } from '../../core/services/config.service';
import { ViewOrderButtonRoundComponent } from '../view-order-button-round/view-order-button-round.component';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { TranslocoDirective } from '@ngneat/transloco';
import { FeaturedContentService } from '../../core/services/featured-content.service';
import { FeaturedContent } from '../../core/models/core/featured-content.model';
import { FeaturedContentName } from '../../core/models/core/featured-content-name.enum';
import { InitParamsService } from '../../core/services/init-params.service';
import { StoreInfoComponent } from "../store-info/store-info.component";
import { PremiumService } from '../../loyalty/premium/premium.service';

@Component({
  selector: 'cwo-header-layout',
  standalone: true,
  imports: [
    CommonModule,
    HeaderLogoComponent,
    HeaderNavigationalButtonComponent,
    OrderDetailsButtonComponent,
    AccountButtonComponent,
    ViewOrderButtonComponent,
    ViewOrderButtonRoundComponent,
    IconComponent,
    TranslocoDirective,
    StoreInfoComponent
],
  templateUrl: './header-layout.component.html',
  styleUrl: './header-layout.component.scss',
  animations: [slideInLeft()],
})
export class HeaderLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly header = viewChild('header', { read: ElementRef });
  readonly headerPrimaryGroup = viewChild('headerPrimaryGroup', { read: ElementRef });
  readonly headerSecondaryGroup = viewChild('headerSecondaryGroup', { read: ElementRef });
  readonly mainNavigation = viewChild('mainNavigation', { read: ElementRef });

  layout = inject(LayoutService);
  modalService = inject(ModalService);
  configService = inject(ConfigService);
  initParamsService = inject(InitParamsService);
  isMobile = signal(false);

  private featuredContentService = inject(FeaturedContentService);
  headerFeaturedContentLinks: FeaturedContent[] = [];

  openCart() {
    this.modalService.toggleModal(ModalName.Cart, undefined, true);
  }

  loyalty = this.configService.loyaltyState();
  store = this.configService.configState().store;


  premiumService = inject(PremiumService);

  //account menu button flags
  isLoggedIn = false;
  accountMenuExpanded = false;
  iconNameType = IconNameType;
  roundIconType = RoundIconType;
  orderService = inject(OrderService);

  storeIdService = inject(StoreIdService);

  order = this.orderService.order;
  isSelfOrder = computed(() => this.order().isInitialized && this.order().isSelfOrderSource);
  orderTypeLabel = computed(() => {
    const orderType = this.order().orderType;
    return this.configService.findOrderTypeConfig(orderType)?.displayName ?? orderType;
  });
  isCheckout = false;
  private resizeObserver?: ResizeObserver;
  shouldForceHideNavigation = false;
  shouldForceHideStoreInfo = false;
  private DESKTOP_BREAKPOINT = 1200;
  private changeDetector = inject(ChangeDetectorRef);

  private router = inject(Router);

  constructor() {
    if (this.initParamsService.isEmbedded) this.isMobile.set(false);
    else this.isMobile.set(this.layout.mobileView());
  }

  ngOnInit(): void {
    this.isCheckout = this.router.url.includes('/checkout');

    this.router.events.subscribe(() => {
      this.isCheckout = this.router.url.includes('/checkout');
    });
    this.headerFeaturedContentLinks = this.featuredContentService.getFeaturedContentsByAreaName(FeaturedContentName.HEADER_LINKS);
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.checkNavigationOverflow());
    [this.header(), this.headerPrimaryGroup(), this.headerSecondaryGroup(), this.mainNavigation()]
      .forEach(el => el?.nativeElement && this.resizeObserver!.observe(el.nativeElement));
    setTimeout(() => this.checkNavigationOverflow(), 0);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  checkNavigationOverflow(): void {
    const headerEl = this.header()?.nativeElement as HTMLElement | undefined;
    const primaryEl = this.headerPrimaryGroup()?.nativeElement as HTMLElement | undefined;
    const secondaryEl = this.headerSecondaryGroup()?.nativeElement as HTMLElement | undefined;
    const navEl = this.mainNavigation()?.nativeElement as HTMLElement | undefined;
    const elementsMissing = !headerEl || !primaryEl || !secondaryEl || !navEl;
    if (elementsMissing) {
      return;
    }

    if (window.innerWidth < this.DESKTOP_BREAKPOINT) {
      this.shouldForceHideNavigation = false;
      this.shouldForceHideStoreInfo = false;
      this.changeDetector.markForCheck();
      return;
    }

    const toggleEl = headerEl.querySelector<HTMLElement>('.menu-toggle-button');
    const storeInfoEl = secondaryEl.querySelector<HTMLElement>('cwo-store-info');
    if (!toggleEl) return;

    const wasNavigationHidden = this.shouldForceHideNavigation;
    const wasStoreInfoHidden = this.shouldForceHideStoreInfo;
    const setNavigationHidden = (hidden: boolean) => {
      headerEl.classList.toggle('force-collapsed', hidden);
      navEl.classList.toggle('force-hide', hidden);
      toggleEl.classList.toggle('force-show', hidden);
    };
    const requiredWidth = (collapsed: boolean) => {
      const styles = getComputedStyle(headerEl);
      const primaryStyles = getComputedStyle(primaryEl);
      const pixels = (value: string) => parseFloat(value) || 0;
      return primaryEl.getBoundingClientRect().width
        + secondaryEl.getBoundingClientRect().width
        + pixels(primaryStyles.marginLeft) + pixels(primaryStyles.marginRight)
        + pixels(styles.paddingLeft) + pixels(styles.paddingRight)
        + pixels(styles.columnGap) * (collapsed ? 2 : 1)
        + (collapsed ? toggleEl.getBoundingClientRect().width : 0);
    };

    try {
      setNavigationHidden(false);
      storeInfoEl?.classList.remove('force-hide');
      this.shouldForceHideNavigation = requiredWidth(false) > headerEl.clientWidth;
      this.shouldForceHideStoreInfo = false;

      if (this.shouldForceHideNavigation) {
        setNavigationHidden(true);
        this.shouldForceHideStoreInfo = !!storeInfoEl && requiredWidth(true) > headerEl.clientWidth;
      }
    } finally {
      setNavigationHidden(wasNavigationHidden);
      storeInfoEl?.classList.toggle('force-hide', wasStoreInfoHidden);
    }
    this.changeDetector.markForCheck();
  }

  toggleLoggedIn() {
    this.isLoggedIn = !this.isLoggedIn;
  }

  accountButtonCallback() {
    this.accountMenuExpanded = !this.accountMenuExpanded;
  }

  openMobileSidebar() {
    this.modalService.toggleModal(ModalName.MobileSideNav, undefined, true);
  }
}
